import * as React from 'react'
import { ClipboardList, Sparkles } from 'lucide-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import {
    isGraphOnlyMode,
    isHideAiActions,
    isHideProjectNotes,
    isInIframe,
    emitEvent,
} from '../LeftMenuMigration/legacyBridge'
import { usePanelStore } from '../../stores/usePanelStore'
import PillButton from './PillButton'

const isProjectNotesOpenSelector = state => state.isProjectNotesOpen
const isAiChatOpenSelector = state => state.isAiChatOpen
const toggleAiChatSelector = state => state.toggleAiChat

/**
 * Floating panel toggles: two action pills for Project Notes and AI Insights.
 *
 * Render gates: caller is responsible for `isOnGraphPage`. Graph-only mode
 * (`?hide_max=1` or `window.top !== window.self`) is short-circuited here.
 * Each pill is independently suppressed by its embed-chrome flag:
 * `?hide_ai_actions=1` drops the AI Insights pill, `?hide_project_notes=1`
 * drops the Project Notes pill. When both are dropped the component renders
 * nothing.
 *
 * Mobile (≤830px): icon-only, vertical stack, anchored bottom-left with
 * safe-area insets; slightly larger hit targets than the old side rail.
 * Desktop: icon + caption, horizontal row at the bottom, centered
 * horizontally (offset to 45% to clear the InfraNodus analytics panel on
 * the right).
 *
 * Active state: when the corresponding panel is open, the pill gets a
 * tinted background, accent border, and intensified icon container — no
 * ring (per design). `:focus-visible` carries keyboard a11y instead.
 *
 * Z-index: 1199 — sits below MUI's drawer (1200) and the chat widget
 * (drawer + 1 = 1201) so the chat can overlay it when expanded, while
 * staying above the canvas/legacy-app.
 *
 * Tailwind: classes use `tw-` prefix; preflight is disabled here, so any
 * `tw-border` must be paired with `tw-border-solid` (see
 * `project_tailwind_preflight_disabled.md`).
 */
export default function PanelToggles() {
    // Compact (icon-only, side-anchored) layout for narrow viewports OR when
    // embedded in an iframe — embeds get the smaller mobile-style pills
    // regardless of the host window's width.
    const isNarrow = useMediaQuery('(max-width:830px)')
    const [inIframe] = React.useState(() => isInIframe())
    const isMobile = isNarrow || inIframe
    const [graphOnly] = React.useState(() => isGraphOnlyMode())
    // Embed-chrome flags — each suppresses its own pill (read once on mount;
    // they're URL-derived and don't change during a session).
    const [hideAi] = React.useState(() => isHideAiActions())
    const [hideNotes] = React.useState(() => isHideProjectNotes())

    const isProjectNotesOpen = usePanelStore(isProjectNotesOpenSelector)
    const isAiChatOpen = usePanelStore(isAiChatOpenSelector)
    const toggleAiChat = usePanelStore(toggleAiChatSelector)

    const showNotes = !hideNotes
    const showAi = !hideAi

    // Graph-only hides everything; otherwise nothing to render once both
    // pills are individually suppressed.
    if (graphOnly || (!showNotes && !showAi)) return null

    const onProjectNotesClick = () => emitEvent('toggleProjectNotesPanel')
    const onAiInsightsClick = () => toggleAiChat()

    // Desktop: inline `transform` centers the bar — Tailwind translate utilities
    // depend on CSS vars from preflight, which is disabled here. Mobile: fixed
    // `bottom` / `left` with safe-area via `max()` (no transform).
    const containerStyle = isMobile
        ? {
              zIndex: 1199,
              bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
              left: 'max(4px, env(safe-area-inset-left, 0px))',
          }
        : { zIndex: 1199, left: '40%', transform: 'translateX(-50%)' }

    return (
        <div className="project-notes-scope">
            <div
                data-testid="panel-toggles"
                className={
                    isMobile
                        ? 'tw-fixed tw-flex tw-flex-col tw-items-center tw-space-y-1.5 tw-pointer-events-auto'
                        : 'tw-fixed tw-bottom-4 tw-mb-1 tw-flex tw-items-center tw-space-x-3 tw-pointer-events-auto'
                }
                style={containerStyle}
            >
                {showNotes && (
                <PillButton
                    isOpen={isProjectNotesOpen}
                    isMobile={isMobile}
                    onClick={onProjectNotesClick}
                    ariaLabel="Project notes"
                    tooltipTitle="Save findings & AI summaries"
                    accentVar="--notes-color"
                    accentRgbVar="--notes-color-rgb"
                    dataTestid="panel-toggle-notes"
                >
                    <span
                        className={`tw-flex tw-items-center tw-justify-center tw-rounded-xl ${
                            isMobile ? 'tw-p-2.5' : 'tw-p-2'
                        } ${
                            isProjectNotesOpen
                                ? 'tw-bg-[rgb(var(--pill-accent-rgb)/0.25)]'
                                : 'tw-bg-[rgb(var(--pill-accent-rgb)/0.15)]'
                        }`}
                    >
                        <ClipboardList
                            size={isMobile ? 15 : 16}
                            className="tw-text-[var(--pill-accent)]"
                        />
                    </span>
                    {!isMobile && (
                        <span className="tw-text-sm tw-font-semibold tw-text-[var(--pill-accent)]">
                            Project Notes
                        </span>
                    )}
                </PillButton>
                )}

                {showAi && (
                <PillButton
                    isOpen={isAiChatOpen}
                    isMobile={isMobile}
                    onClick={onAiInsightsClick}
                    ariaLabel="AI Insights"
                    tooltipTitle="Chat with AI about your content"
                    accentVar="--ai-buttons-color"
                    accentRgbVar="--ai-buttons-color-rgb"
                    dataTestid="panel-toggle-ai"
                >
                    {!isMobile && (
                        <span className="tw-text-sm tw-font-semibold tw-text-[var(--pill-accent)]">
                            AI Insights
                        </span>
                    )}
                    {/* AI's icon container is a SOLID brand fill, not the rgb-channel
                        tint pattern used for Notes — it has its own *-strong shade
                        for the active state. References --ai-buttons-color directly
                        because --pill-accent-strong isn't a generic abstraction. */}
                    <span
                        className={`tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-shadow-sm ${
                            isMobile ? 'tw-p-2.5' : 'tw-p-2'
                        } ${
                            isAiChatOpen
                                ? 'tw-bg-[var(--ai-buttons-color-strong)]'
                                : 'tw-bg-[var(--pill-accent)]'
                        }`}
                    >
                        <Sparkles
                            size={isMobile ? 15 : 16}
                            className="tw-text-white"
                        />
                    </span>
                </PillButton>
                )}
            </div>
        </div>
    )
}
